// AssoAnnuaire.jsx — Annuaire enrichi (Phase B)
// Cartes : photo membre + logo entreprise + filtres avancés + modal détail avec actions
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { usePermissions } from '@/lib/usePermissions';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import {
  Building2, Search, Lock, Loader2, Plus, MapPin, X, Upload, Pencil, Heart,
  Filter, Star, ShieldOff,
} from 'lucide-react';
import FicheDetailModal from '@/components/FicheDetailModal';

const SECTEURS_DEFAUT = [
  'Artisanat','Bâtiment & Construction','Commerce','Conseil & Services aux entreprises',
  'Distribution','Évènementiel','Finance & Assurance','Immobilier',
  'Industrie','Marketing & Communication','Restauration & Hôtellerie',
  'Santé & Bien-être','Services à la personne','Tech & Digital','Transport & Logistique','Autre',
];

export default function AssoAnnuaire() {
  const { user } = useAuth();
  const { can, loading: permsLoading } = usePermissions();
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ secteurs: [], villes: [], metiers: [] });
  const [favIds, setFavIds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [secteur, setSecteur] = useState('');
  const [ville, setVille] = useState('');
  const [metier, setMetier] = useState('');
  const [entreprise, setEntreprise] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editingMine, setEditingMine] = useState(false);
  const [myFiche, setMyFiche] = useState(null);

  const loadFilters = async () => {
    try {
      const [d, m, s, f] = await Promise.all([
        api.get('/annuaire/filters/distinct'),
        api.get('/annuaire/me').catch(() => ({ data: null })),
        api.get('/annuaire/stats/overview').catch(() => ({ data: null })),
        api.get('/networking/favoris/ids').catch(() => ({ data: [] })),
      ]);
      setFilters(d.data);
      setMyFiche(m.data);
      setStats(s.data);
      setFavIds(f.data);
    } catch {}
  };
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/annuaire', { params: {
        search: search || undefined, secteur: secteur || undefined,
        ville: ville || undefined, metier: metier || undefined, entreprise: entreprise || undefined,
      } });
      setList(data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadFilters(); }, []);
  useEffect(() => { if (!permsLoading) load(); /* eslint-disable-next-line */ }, [search, secteur, ville, metier, entreprise, permsLoading]);

  const reset = () => { setSearch(''); setSecteur(''); setVille(''); setMetier(''); setEntreprise(''); };
  const hasFilters = !!(search || secteur || ville || metier || entreprise);

  const toggleFav = async (ficheId) => {
    try {
      const { data } = await api.post(`/networking/${ficheId}/favori`);
      setFavIds((ids) => data.favori ? [...ids, ficheId] : ids.filter((x) => x !== ficheId));
      toast.success(data.favori ? 'Ajouté aux favoris' : 'Retiré des favoris');
    } catch { toast.error('Erreur'); }
  };

  if (permsLoading) return <div className="p-8" />;
  if (!can('annuaire', 'view')) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="annuaire-denied">
        <div className="card text-center py-12">
          <Lock className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          <h3 className="font-display text-lg font-bold text-[#0F172A]">Accès refusé</h3>
        </div>
      </div>
    );
  }
  const canModerate = can('annuaire', 'moderate');

  return (
    <div className="p-4 sm:p-8 lg:p-12 max-w-7xl mx-auto" data-testid="annuaire-page">
      {/* En-tête éditorial */}
      <div className="flex items-start justify-between mb-8 slide-up flex-wrap gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--theme-primary)' }}>Annuaire</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-[#0F172A] leading-[1.02]">
            {stats?.total || list.length} <span className="italic font-medium" style={{ color: 'var(--theme-primary)' }}>entreprises.</span>
          </h1>
          <p className="text-sm text-[#475569] mt-2">Découvrez le réseau et entrez en contact avec les membres.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setEditingMine(true)} className="btn-secondary" data-testid="my-fiche-btn">
            {myFiche ? <><Pencil className="w-4 h-4" /> Ma fiche</> : <><Plus className="w-4 h-4" /> Créer ma fiche</>}
          </button>
        </div>
      </div>

      {/* Recherche + filtres */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px] flex items-center gap-3 px-3 py-2 rounded-xl border border-[#E2E8F0] bg-[#F9F9F8]">
            <Search className="w-4 h-4 text-[#94A3B8]" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher (entreprise, nom, métier, mots-clés…)"
              className="flex-1 outline-none text-sm bg-transparent"
              data-testid="annuaire-search"
            />
            {search && <button onClick={() => setSearch('')} className="text-[#94A3B8] hover:text-[#475569]"><X className="w-4 h-4" /></button>}
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`btn-secondary !text-xs ${showFilters || hasFilters ? '!border-[var(--theme-primary)] !text-[var(--theme-primary)]' : ''}`} data-testid="toggle-filters">
            <Filter className="w-3.5 h-3.5" /> Filtres
            {hasFilters && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[var(--theme-primary)] text-white text-[9px] font-black">{[secteur, ville, metier, entreprise].filter(Boolean).length}</span>}
          </button>
          {hasFilters && <button onClick={reset} className="btn-ghost !text-xs">Réinitialiser</button>}
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4 pt-4 border-t border-[#F1F5F9]" data-testid="advanced-filters">
            <Select label="Secteur" value={secteur} onChange={setSecteur} options={filters.secteurs.length ? filters.secteurs : SECTEURS_DEFAUT} testid="filter-secteur" />
            <Select label="Ville" value={ville} onChange={setVille} options={filters.villes} testid="filter-ville" />
            <Select label="Métier" value={metier} onChange={setMetier} options={filters.metiers} testid="filter-metier" />
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">Entreprise</label>
              <input type="text" value={entreprise} onChange={(e) => setEntreprise(e.target.value)} className="ap-input" placeholder="Filtrer par nom…" data-testid="filter-entreprise" />
            </div>
          </div>
        )}
      </div>

      {/* Grille de cartes */}
      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
      ) : list.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="annuaire-grid">
          {list.map((f) => (
            <FicheCard
              key={f.id} fiche={f}
              isFav={favIds.includes(f.id)}
              onToggleFav={() => toggleFav(f.id)}
              onOpen={() => setSelected(f)}
              canModerate={canModerate}
              isMine={f.user_id === user?.id}
            />
          ))}
        </div>
      )}

      {selected && (
        <FicheDetailModal
          ficheId={selected.id}
          initialFiche={selected}
          isFav={favIds.includes(selected.id)}
          onToggleFav={() => toggleFav(selected.id)}
          canModerate={canModerate}
          onClose={() => { setSelected(null); load(); loadFilters(); }}
        />
      )}
      {editingMine && (
        <EditMyFicheModal initial={myFiche} onClose={() => { setEditingMine(false); loadFilters(); }} />
      )}
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="ap-input" data-testid={testid}>
        <option value="">— Tous —</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function FicheCard({ fiche, isFav, onToggleFav, onOpen, canModerate, isMine }) {
  return (
    <div
      onClick={onOpen}
      className={`group bg-white border rounded-3xl p-5 cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1 relative ${fiche.mise_en_avant ? 'border-2' : 'border-[#E2E8F0]'}`}
      style={fiche.mise_en_avant ? { borderColor: 'var(--theme-primary)' } : {}}
      data-testid={`fiche-card-${fiche.id}`}
    >
      {/* Badges */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        {fiche.mise_en_avant && (
          <span className="badge badge-primary !text-[9px] !px-2"><Star className="w-2.5 h-2.5" /> En avant</span>
        )}
        {!isMine && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFav(); }}
            className={`w-8 h-8 rounded-full grid place-items-center transition-all ${isFav ? 'bg-[#FEE2E2] text-[#DC2626]' : 'bg-[#F1F5F9] text-[#94A3B8] hover:bg-[#FEE2E2] hover:text-[#DC2626]'}`}
            title={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            data-testid={`fav-btn-${fiche.id}`}
          >
            <Heart className="w-4 h-4" fill={isFav ? 'currentColor' : 'none'} />
          </button>
        )}
      </div>

      {/* Photo membre + Logo entreprise */}
      <div className="flex items-start gap-3 mb-4 pr-16">
        {/* Photo membre (rond) */}
        {fiche.photo_membre ? (
          <img src={fiche.photo_membre} alt={fiche.prenom} className="w-14 h-14 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0" />
        ) : (
          <div className="w-14 h-14 rounded-full grid place-items-center font-display font-black text-sm shrink-0 ring-2 ring-white shadow-sm" style={{ background: 'var(--theme-primary-soft)', color: 'var(--theme-primary)' }}>
            {(fiche.prenom?.[0] || '?') + (fiche.nom?.[0] || '')}
          </div>
        )}
        {/* Logo entreprise (carré, plus petit, décalé) */}
        {fiche.logo_url && (
          <img src={fiche.logo_url} alt={fiche.nom_entreprise} className="w-10 h-10 rounded-lg object-contain bg-white border border-[#E2E8F0] shrink-0 -ml-3 mt-3" />
        )}
      </div>

      <div className="min-w-0 mb-3">
        <h3 className="font-display font-bold text-[#0F172A] text-base leading-tight mb-0.5 truncate">{fiche.nom_entreprise}</h3>
        {(fiche.prenom || fiche.nom) && (
          <div className="text-xs text-[#475569] truncate">
            {fiche.prenom} {fiche.nom}
            {fiche.metier && <span className="text-[#94A3B8]"> · {fiche.metier}</span>}
          </div>
        )}
      </div>

      <div className="text-[10px] uppercase tracking-[0.14em] font-bold mb-3 truncate" style={{ color: 'var(--theme-primary)' }}>
        {fiche.secteur}{fiche.ville && ` · ${fiche.ville}`}
      </div>

      {fiche.description && <p className="text-xs text-[#475569] line-clamp-2 leading-relaxed mb-3">{fiche.description}</p>}

      {fiche.statut === 'suspendu' && canModerate && (
        <div className="text-xs text-[#B45309] flex items-center gap-1 mt-2">
          <ShieldOff className="w-3 h-3" /> Suspendue
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-3xl p-16 text-center">
      <Building2 className="w-10 h-10 mx-auto mb-3 text-[#CBD5E1]" />
      <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2">Aucune entreprise trouvée</h3>
      <p className="text-sm text-[#475569]">Modifiez les filtres ou créez votre fiche pour démarrer.</p>
    </div>
  );
}

function EditMyFicheModal({ initial, onClose }) {
  const [form, setForm] = useState(initial || {
    nom_entreprise: '', metier: '', secteur: 'Autre', description: '', mots_cles: '',
    ville: '', telephone_pro: '', email_pro: '', site_web: '',
    linkedin: '', instagram: '', facebook: '',
    logo_url: '', photo_membre: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/annuaire/me', form);
      toast.success('Fiche enregistrée');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4 fade-in" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl p-7 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="edit-fiche-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-2xl font-bold text-[#0F172A]">{initial ? 'Modifier ma fiche' : 'Créer ma fiche'}</h3>
          <button type="button" onClick={onClose} className="text-[#94A3B8]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          {/* Photos */}
          <div className="grid grid-cols-2 gap-3">
            <FieldWithUpload label="Ma photo (perso)" value={form.photo_membre} onChange={(v) => setForm({ ...form, photo_membre: v })} testid="photo-membre" />
            <FieldWithUpload label="Logo entreprise" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} testid="logo-entreprise" />
          </div>
          <Field label="Nom entreprise *">
            <input required type="text" value={form.nom_entreprise} onChange={(e) => setForm({ ...form, nom_entreprise: e.target.value })} className="ap-input" data-testid="fiche-nom-entreprise" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Métier"><input type="text" value={form.metier} onChange={(e) => setForm({ ...form, metier: e.target.value })} className="ap-input" placeholder="Ex: Boulanger" /></Field>
            <Field label="Secteur">
              <select value={form.secteur} onChange={(e) => setForm({ ...form, secteur: e.target.value })} className="ap-input">
                {SECTEURS_DEFAUT.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description (2-3 phrases)">
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ap-input" maxLength={500} />
          </Field>
          <Field label="Mots-clés (séparés par virgule)">
            <input type="text" value={form.mots_cles} onChange={(e) => setForm({ ...form, mots_cles: e.target.value })} className="ap-input" placeholder="pain, bio, local, livraison" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Téléphone"><input type="tel" value={form.telephone_pro} onChange={(e) => setForm({ ...form, telephone_pro: e.target.value })} className="ap-input" /></Field>
            <Field label="Email pro"><input type="email" value={form.email_pro} onChange={(e) => setForm({ ...form, email_pro: e.target.value })} className="ap-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Site web"><input type="url" value={form.site_web} onChange={(e) => setForm({ ...form, site_web: e.target.value })} className="ap-input" placeholder="https://…" /></Field>
            <Field label="Ville"><input type="text" value={form.ville} onChange={(e) => setForm({ ...form, ville: e.target.value })} className="ap-input" /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="LinkedIn"><input type="url" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} className="ap-input" placeholder="URL" /></Field>
            <Field label="Instagram"><input type="url" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="ap-input" placeholder="URL" /></Field>
            <Field label="Facebook"><input type="url" value={form.facebook} onChange={(e) => setForm({ ...form, facebook: e.target.value })} className="ap-input" placeholder="URL" /></Field>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-6 pt-5 border-t border-[#F1F5F9]">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="fiche-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}</button>
        </div>
      </form>
    </div>
  );
}

function FieldWithUpload({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('Max 5MB');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.url);
    } catch { toast.error('Upload échoué'); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">{label}</label>
      {value ? (
        <div className="relative">
          <img src={value} alt={label} className="w-full h-24 rounded-xl object-cover bg-[#F1F5F9] border border-[#E2E8F0]" />
          <button type="button" onClick={() => onChange('')} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-[#0F172A]/80 text-white grid place-items-center hover:bg-[#0F172A]"><X className="w-3 h-3" /></button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center h-24 rounded-xl border-2 border-dashed border-[#E2E8F0] cursor-pointer hover:border-[var(--theme-primary)] hover:bg-[#F9F9F8] transition-colors" data-testid={`upload-${testid}`}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin text-[#94A3B8]" /> : <><Upload className="w-4 h-4 text-[#94A3B8] mb-1" /><span className="text-[10px] text-[#94A3B8]">PNG, JPG (5MB)</span></>}
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
        </label>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-[#475569] mb-2">{label}</label>
      {children}
    </div>
  );
}
